import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { DataTable } from "../components/DataTable";
import { api, type ItemTreeLevel } from "../lib/api";
import { int } from "../lib/format";
import { useViewSync, type ViewSyncState } from "../lib/view-sync";
import { useDashboard } from "../pages/Dashboard";

/** Sertica's Item Info (SPEC §8.8): every item, not only the user's units'. A row opens the item. */
export function ItemsTab() {
  const { open } = useDashboard();
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">
        Every stock item in Sertica. Only the search applies here: it matches the item number, name, reference and drawing numbers and manufacturer.
      </p>
      <DataTable table="items" title="Items" lens="plain" onRow={(r) => open({ type: "item", no: String(r.no) })} />
    </div>
  );
}

export function ItemGroupsTab() {
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">
        The item groups of Sertica&apos;s item tree, read a group at a time: until the whole tree has been read, deeper groups are still missing. Items counts the items directly in a group, not in its subgroups.
      </p>
      <DataTable table="item-groups" title="Item groups" lens="plain" />
    </div>
  );
}

export function MeasuringUnitsTab() {
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">The measuring units the items use, with how many items use each.</p>
      <DataTable table="measuring-units" title="Measuring units" lens="plain" />
    </div>
  );
}

const treeQuery = (parent?: string) => ({
  queryKey: ["item-tree", parent ?? ""],
  queryFn: () => api<ItemTreeLevel>(`/item-tree${parent ? `?parent=${encodeURIComponent(parent)}` : ""}`),
});

/** Reads a level from Sertica as it is opened (SPEC §8.8), then reloads it from the store. */
function useTreeSync(no: string | null, parent?: string) {
  const qc = useQueryClient();
  return useViewSync(no ? { kind: "item-group", no } : null, () => void qc.invalidateQueries({ queryKey: ["item-tree", parent ?? ""] }));
}

function TreeSyncNote({ s, pad }: { s: ViewSyncState; pad: number }) {
  if (s.status !== "syncing" && s.status !== "failed" && s.status !== "unavailable") return null;
  return (
    <li className={`py-[3px] text-[11.5px] ${s.status === "syncing" ? "text-muted" : "text-[var(--danger)]"}`} style={{ paddingLeft: pad }} role="status">
      {s.status === "syncing" ? "Reading from Sertica…" : s.status === "unavailable" ? "Sync service is not running: showing stored data" : "Couldn’t reach Sertica: showing stored data"}
    </li>
  );
}

/** Sertica's Item Tree: groups open to their subgroups and items, a level at a time. */
export function ItemTreeTab() {
  const q = useQuery(treeQuery());
  const sync = useTreeSync("*");
  return (
    <section className="border border-line bg-card" aria-label="Item tree">
      <div className="toolbar flex items-center gap-3 px-3 h-10">
        <h3 className="flex items-center gap-2 text-[13px] font-medium">
          Item tree
          {q.data && <span className="tnum rounded-full bg-white/15 px-2 text-[12px] leading-5">{int(q.data.groups.length)}</span>}
        </h3>
      </div>
      <div className="py-1 text-[12.5px]">
        {q.isError ? (
          <p className="px-3 py-4 text-[var(--danger)]" role="alert">
            Couldn&apos;t load the item tree.{" "}
            <button className="underline font-semibold" onClick={() => void q.refetch()}>
              Retry
            </button>
          </p>
        ) : !q.data ? (
          <p className="px-3 py-4 text-muted">Loading…</p>
        ) : !q.data.groups.length ? (
          <p className="px-3 py-4 text-muted">{sync.status === "syncing" ? "Reading the item groups from Sertica…" : "The item tree has not been read from Sertica yet."}</p>
        ) : (
          <ul role="tree">
            <TreeSyncNote s={sync} pad={12} />
            {q.data.groups.map((g) => (
              <GroupNode key={g.no} group={g} depth={0} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

type Group = ItemTreeLevel["groups"][number];

function GroupNode({ group, depth }: { group: Group; depth: number }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ ...treeQuery(group.no), enabled: open });
  const sync = useTreeSync(open ? group.no : null, group.no);
  const openItem = useDashboard().open;
  const pad = { paddingLeft: 12 + depth * 18 };
  const inner = 12 + (depth + 1) * 18 + 20;
  return (
    <li role="treeitem" aria-expanded={group.hasChildren ? open : undefined}>
      <button
        className="w-full flex items-center gap-2 pr-3 py-[5px] text-left hover:bg-hover disabled:cursor-default"
        style={pad}
        disabled={!group.hasChildren}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden className="w-3 text-muted">
          {group.hasChildren ? (open ? "▾" : "▸") : ""}
        </span>
        <span className="font-medium text-ink">{group.name ?? group.no}</span>
        {group.name && !group.name.startsWith(group.no) && <span className="text-muted">{group.no}</span>}
        <span className="flex-1" />
        <span className="text-[11.5px] text-muted tnum">
          {group.subgroups > 0 && `${int(group.subgroups)} ${group.subgroups === 1 ? "group" : "groups"}`}
          {group.subgroups > 0 && group.itemCount ? " · " : ""}
          {!!group.itemCount && `${int(group.itemCount)} ${group.itemCount === 1 ? "item" : "items"}`}
        </span>
      </button>
      {open && (
        <ul role="group">
          <TreeSyncNote s={sync} pad={inner} />
          {!q.data ? (
            <li className="py-[5px] text-muted" style={{ paddingLeft: inner }}>
              {q.isError ? "Couldn't load this group." : "Loading…"}
            </li>
          ) : (
            <>
              {q.data.groups.map((g) => (
                <GroupNode key={g.no} group={g} depth={depth + 1} />
              ))}
              {q.data.items.map((i) => (
                <li key={i.no} role="treeitem">
                  <button className="w-full flex items-center gap-2 pr-3 py-[3px] text-left text-ink2 hover:bg-hover" style={{ paddingLeft: inner }} onClick={() => openItem({ type: "item", no: i.no })}>
                    <span className="tnum text-ink">{i.no}</span>
                    <span className={i.inactive ? "text-muted line-through" : ""}>{i.name}</span>
                    {i.critical && <span className="rounded-badge border border-line px-1.5 text-[11px]">Critical</span>}
                    {i.unit && <span className="text-muted">{i.unit}</span>}
                  </button>
                </li>
              ))}
              {q.data.itemsTotal > q.data.items.length && (
                <li className="py-[3px] text-[11.5px] text-muted" style={{ paddingLeft: inner }}>
                  And {int(q.data.itemsTotal - q.data.items.length)} more: search for them under Item.
                </li>
              )}
              {sync.status !== "syncing" && !q.data.groups.length && !q.data.items.length && (
                <li className="py-[3px] text-[11.5px] text-muted" style={{ paddingLeft: inner }}>
                  No items.
                </li>
              )}
            </>
          )}
        </ul>
      )}
    </li>
  );
}
